/** @module Sign-in screen; reachable without a session, like the registration. */
import { useNavigate } from "@tanstack/react-router";
import { type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import { Switch } from "@/components/ui/controls";
import { AuthLayout, PasswordInput } from "@/features/auth/auth-layout";
import { signIn } from "@/lib/session";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password, remember);
      await navigate({ to: "/" });
    } catch (cause) {
      // Never say which half was wrong; that would help someone guessing.
      const code = cause instanceof ApiError ? cause.code : "unexpected_response";
      setError(t(`auth.error.${code}`, { defaultValue: t("auth.error.generic") }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={t("app.name")} lead={t("auth.tagline")} footer={t("auth.inviteOnly")}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <label className="field">
          <span className="field-label">{t("auth.email")}</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            className="input"
          />
        </label>
        <div className="field">
          <label htmlFor="password" className="field-label">
            {t("auth.password")}
          </label>
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
        </div>
        <div className="row-between min-h-11">
          <span>{t("auth.remember")}</span>
          <Switch checked={remember} label={t("auth.remember")} onChange={setRemember} />
        </div>
        {error ? (
          <p role="alert" className="callout callout-warn">
            {error}
          </p>
        ) : null}
        <button type="submit" disabled={busy} className="btn btn-primary btn-block">
          {busy ? t("auth.signingIn") : t("auth.signIn")}
        </button>
      </form>
    </AuthLayout>
  );
}
